/**
 * Project Management Hooks
 * React Query hooks for project operations using Edge Functions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import type { Project } from '../types/database';

// Get all projects for the current user with PERFORMANCE OPTIMIZATIONS
export const useProjects = () => {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const startTime = performance.now();
      console.log('🚀 [PERF] Starting projects API call...');
      
      try {
        const result = await apiClient.getProjects();
        
        // Handle Edge Function response format
        let projectsData = result;
        if (result && typeof result === 'object' && 'success' in result && 'data' in result) {
          projectsData = result.data;
        }
        
        // Validate and sanitize project data to prevent toString() errors
        if (!Array.isArray(projectsData)) {
          console.warn('⚠️ API returned non-array projects data:', result);
          return [];
        }
        
        // Filter out invalid projects and ensure all required fields exist
        const validProjects = projectsData.filter((project: any) => {
          if (!project || typeof project !== 'object') {
            console.warn('⚠️ Invalid project object:', project);
            return false;
          }
          
          if (project.id == null || isNaN(Number(project.id))) {
            console.warn('⚠️ Project has invalid ID:', project);
            return false;
          }
          
          if (!project.name || typeof project.name !== 'string') {
            console.warn('⚠️ Project has invalid name:', project);
            return false;
          }
          
          return true;
        }).map((project: any) => ({
          ...project,
          id: Number(project.id), // Ensure ID is a number
          name: String(project.name), // Ensure name is a string
          description: project.description ? String(project.description) : '',
          currency: project.currency ? String(project.currency) : 'USD'
        }));
        
        const duration = performance.now() - startTime;
        console.log(`✅ [PERF] Projects loaded: ${validProjects.length} projects in ${duration.toFixed(2)}ms`);
        return validProjects;
        
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(`❌ [PERF] Projects failed in ${duration.toFixed(2)}ms:`, error);
        return []; // Return empty array on error
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - Increased for better caching
    gcTime: 15 * 60 * 1000, // 15 minutes - Keep in cache longer  
    refetchOnWindowFocus: false, // Reduce unnecessary refetches
    refetchOnMount: false, // Only refetch if stale
    refetchInterval: false, // Disable automatic polling
    retry: (failureCount, error) => {
      // Intelligent retry logic
      if (failureCount >= 3) return false;
      if (error instanceof Error && error.message.includes('401')) return false;
      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

// Create a new project
export const useCreateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (projectData: {
      name: string;
      description?: string;
      currency?: string;
    }) => apiClient.createProject(projectData),
    onSuccess: () => {
      // Invalidate projects query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// Update an existing project
export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (projectData: {
      id: number;
      name: string;
      description?: string;
      currency?: string;
    }) => apiClient.updateProject(projectData.id, projectData),
    onSuccess: () => {
      // Invalidate projects query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// Real-time project updates using Supabase subscriptions
export const useProjectSubscription = () => {
  const queryClient = useQueryClient();
  
  // This will be implemented with Supabase real-time subscriptions
  // For now, we'll use polling
  return useQuery({
    queryKey: ['projects', 'realtime'],
    queryFn: () => apiClient.getProjects(),
    refetchInterval: 30000, // Poll every 30 seconds
    refetchOnWindowFocus: true,
  });
};
